/**
 * Aspect — Surface stacking helpers (lamp on nightstand, etc.)
 * --------------------------------------------------------------
 * Catalog-driven: supports_surface / placement_mode:"surface".
 * Children keep world positions; parent moves apply the same ΔXZ.
 */

import { extentsXZ } from "./furniture_placement.js";

const SUPPORT_ID_RE = /nightstand|sidetable|table|desk|dresser|cabinet|counter|island|tv_stand|tv_table|(?:^|_)stand(?:_|$)/i;
const SURFACE_ITEM_RE =
  /(?:^|_)(tv|lamp|plant|book|vase|candle|clock|remote|mug|bowl|laptop|monitor|phone|speaker|pillow|box|bottle|jar|plate|glass|teapot)(?:_|$)/i;
const SURFACE_ITEM_BLOCK_RE = /fridge|stove|washer|washing|couch|sofa|bed(?!_pillow)|chair|toilet|shower/i;

/** @param {object|null|undefined} metaOrPlacement */
export function isSurfaceSupport(metaOrPlacement) {
  if (!metaOrPlacement) return false;
  if (metaOrPlacement.supports_surface === true) return true;
  const id = metaOrPlacement.asset_id ?? metaOrPlacement.id;
  return typeof id === "string" && SUPPORT_ID_RE.test(id);
}

/** @param {object|null|undefined} metaOrPlacement */
export function isSurfaceItem(metaOrPlacement) {
  if (!metaOrPlacement) return false;
  if (metaOrPlacement.placement_mode === "surface") return true;
  // Wall/ceiling fixtures are never table-top items (lamp_2 is a ceiling spot).
  if (metaOrPlacement.placement_mode === "wall" || metaOrPlacement.placement_mode === "ceiling") return false;
  const id = String(metaOrPlacement.asset_id ?? metaOrPlacement.id ?? "");
  const cat = String(metaOrPlacement.category ?? "").toLowerCase();
  if (SURFACE_ITEM_BLOCK_RE.test(id)) return false;
  if (/tv_stand/i.test(id)) return false;
  if (/stand|counter|desk|dresser/i.test(id) && !/(?:^|_)tv(?:_|$)/i.test(id)) return false;
  if (/^lamp_/i.test(id) || SURFACE_ITEM_RE.test(id)) return true;
  if (cat === "decor") return true;
  if (cat === "electronics") return true;
  return false;
}

/** World Y of the top face of a floor-anchored placement. */
export function supportTopY(support) {
  const y = Number(support?.position?.[1]) || 0;
  const h = Number(support?.footprint?.h) || 0;
  const scale = Number(support?.scale) > 0 ? Number(support.scale) : 1;
  return y + h * scale;
}

function pointInSupportXZ(x, z, support) {
  const pos = support?.position;
  if (!pos) return false;
  const { ex, ez } = extentsXZ(support.footprint ?? { w: 1, d: 1 }, support.rotation_y ?? 0, support.scale ?? 1);
  // Slight inset so the child stays visually on the top, not hanging off edges.
  const margin = 0.02;
  return Math.abs(x - pos[0]) <= Math.max(0.01, ex - margin) && Math.abs(z - pos[2]) <= Math.max(0.01, ez - margin);
}

/**
 * Find the highest surface-support placement under (x,z).
 * @param {Iterable<object>} placements
 * @param {(p: object) => object|null} [catalogLookup]
 */
export function findSupportAt(x, z, placements, { excludeId = null, catalogLookup = null } = {}) {
  let best = null;
  let bestTop = -Infinity;
  for (const p of placements ?? []) {
    if (!p?.id || p.id === excludeId) continue;
    if (p.parent_id) continue; // never stack on another stacked item
    const meta = catalogLookup?.(p.asset_id) ?? p;
    if (!isSurfaceSupport(meta) && !isSurfaceSupport(p)) continue;
    if (!pointInSupportXZ(x, z, p)) continue;
    const top = supportTopY(p);
    if (top >= bestTop) {
      bestTop = top;
      best = p;
    }
  }
  return best;
}

/** Clamp XZ so a child footprint stays on the parent top. */
export function clampToSupportXZ(x, z, childFp, childRot, childScale, support) {
  if (!support?.position) return { x, z };
  const parent = extentsXZ(support.footprint ?? { w: 1, d: 1 }, support.rotation_y ?? 0, support.scale ?? 1);
  const child = extentsXZ(childFp ?? { w: 0.1, d: 0.1 }, childRot ?? 0, childScale ?? 1);
  const maxEx = Math.max(0.01, parent.ex - child.ex);
  const maxEz = Math.max(0.01, parent.ez - child.ez);
  const cx = support.position[0];
  const cz = support.position[2];
  return {
    x: Math.min(cx + maxEx, Math.max(cx - maxEx, x)),
    z: Math.min(cz + maxEz, Math.max(cz - maxEz, z)),
  };
}

/** Children of a placement id. */
export function childrenOf(parentId, placements) {
  const out = [];
  if (!parentId) return out;
  for (const p of placements ?? []) {
    if (p?.parent_id === parentId) out.push(p);
  }
  return out;
}
